'use client';

import * as React from 'react';
import { useState } from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';
import styles from './LeadList.module.css';

export interface LeadItemProps {
  /** Lead's name */
  name: string;
  /** Lead's company (optional) */
  company?: string;
  /** Interaction stats (e.g., "3 clicks, 2 views (5m 30s)") */
  interactionText: React.ReactNode;
  /** Float/record link shown in its own column next to the stats */
  floatLink?: React.ReactNode;
  /** Date/time of last interaction */
  date: React.ReactNode;
  /** Action buttons (e.g., LinkedIn, Phone, Email icons) */
  actions?: React.ReactNode;
}

export interface LeadListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of lead items to display */
  items: LeadItemProps[];
  /** Optional title displayed above the list (use with your own heading element) */
  title?: string;
  /** Maximum number of items to show before "show all" (default: 6) */
  maxVisible?: number;
  /** Label for the "show all" link (default: "View all top leads") */
  showAllLabel?: string;
  /** Content to display when items array is empty */
  emptyMessage?: React.ReactNode;
  /** Additional class names */
  className?: string;
}

/**
 * LeadList Component
 *
 * A bordered container for displaying lead/contact items with company info,
 * interaction details, and action icons.
 *
 * Specifications:
 * - Container: 32px border radius, 1px black border, 16px padding
 * - Row: 56px height, 32px border radius, 32px gap
 * - Name column: 220px fixed width, 600 font weight
 * - Company text: 10px font size, muted color
 */
export const LeadList = React.forwardRef<HTMLDivElement, LeadListProps>(
  (
    {
      items,
      title,
      maxVisible = 6,
      showAllLabel = 'View all top leads',
      emptyMessage,
      className,
      ...props
    },
    ref
  ) => {
    const [showAll, setShowAll] = useState(false);

    const getInitials = (name: string) =>
      name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('');

    const avatarColors = ['#BCFBC6', '#DDD5FF', '#FFD2E7', '#E1E1E1', '#EDF4FE'];

    const displayedItems = showAll ? items : items.slice(0, maxVisible);
    const hasMore = items.length > maxVisible;

    if (!items.length) {
      return (
        <div ref={ref} className={clsx(styles.container, className)} {...props}>
          <div className={styles.emptyState}>
            {emptyMessage || 'No leads identified yet.'}
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={clsx(styles.container, className)} {...props}>
        <div className={styles.list}>
          {displayedItems.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <hr className={styles.divider} />}
            <div className={styles.row}>
              <div className={styles.avatarColumn}>
                <div
                  className={styles.avatar}
                  style={{ backgroundColor: avatarColors[idx % avatarColors.length] }}
                >
                  {getInitials(item.name)}
                </div>
              </div>
              <div className={styles.nameColumn}>
                <div className={styles.name}>{item.name}</div>
                {item.company && (
                  <div className={styles.company}>{item.company}</div>
                )}
              </div>
              {item.date && (
                <div className={styles.dateColumn}>
                  <div className={styles.date}>{item.date}</div>
                </div>
              )}
              <div className={styles.interactionColumn}>
                <span className={styles.interactionText}>
                  {item.interactionText}
                </span>
              </div>
              {item.floatLink && (
                <div className={styles.floatColumn}>
                  <span className={styles.floatText}>{item.floatLink}</span>
                </div>
              )}
              {item.actions && (
                <div className={styles.actionsColumn}>
                  {item.actions}
                </div>
              )}
            </div>
            </React.Fragment>
          ))}
        </div>
        {hasMore && !showAll && (
          <div>
          <hr className={styles.divider} />
          <div className={styles.showAllContainer}>
            <Button variant="tertiary" onClick={() => setShowAll(true)}>
              {showAllLabel}
            </Button>
          </div>
          </div>
        )}
      </div>
    );
  }
);

LeadList.displayName = 'LeadList';

export default LeadList;
